<template>
  <v-container v-if="prediction">
    <toolbar mode="prediction" :item="prediction" @clone="onClone" @delete="onDelete">
      <template v-if="isOwner(prediction)">
        <regenerate @confirmed="onRegenerate" />
        <ax-button icon="mdi-api" tooltip="Copy APIs" :disabled="prediction.status !== 'SUCCESS'" @clicked="onCopyApis" />
        <ax-button icon="mdi-sync" tooltip="Sync Groups" @clicked="onSyncGroups" />
        <v-btn icon :href="prediction.xlsx" :disabled="!prediction.xlsx"><v-icon>mdi-arrow-down</v-icon></v-btn>
      </template>
    </toolbar>
    <v-card>
      <v-tabs v-model="tab">
        <template v-if="prediction.status == 'SUCCESS'">
          <v-tab href="#data">Data</v-tab>
          <v-tab href="#info">Info</v-tab>
        </template>
        <template v-else>
          <v-tab href="#result">Result</v-tab>
        </template>
        <v-tab href="#logs">Logs</v-tab>
        <v-tab href="#details">Details</v-tab>
      </v-tabs>
      <v-divider />
      <v-tabs-items v-model="tab">
        <template v-if="prediction.status == 'SUCCESS'">
          <v-tab-item value="data">
            <frame-head :items="head" @load="onLoad" />
          </v-tab-item>
          <v-tab-item value="info">
            <frame-info :items="info" />
          </v-tab-item>
        </template>
        <v-tab-item v-if="result" value="result">
          <v-card-text>
            <pre>{{ errors }}</pre>
            <pre v-if="isSuper">{{ result.traceback }}</pre>
          </v-card-text>
        </v-tab-item>
        <v-tab-item value="logs">
          <v-card-text>
            <pre v-if="prediction.history">{{ prediction.history.join("\n") }}</pre>
            <pre v-else-if="prediction.logs && typeof JSON.parse(prediction.logs)[0] === 'string'">{{ JSON.parse(prediction.logs).join("\n") }}</pre>
            <pre v-else>{{ prediction.logs }}</pre>
          </v-card-text>
        </v-tab-item>
        <v-tab-item value="details">
          <v-card-text>
            <v-container fluid class="pa-0">
              <v-row>
                <v-col>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Application</v-col>
                    <v-col>
                      <router-link :to="{ name: 'applicationShow', params: { slug: prediction.application.slug } }">
                        {{ prediction.application.name }}
                      </router-link>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Dataset</v-col>
                    <v-col>
                      <router-link :to="{ name: 'datasetShow', params: { uuid: prediction.dataset.uuid } }">
                        {{ prediction.dataset.name }}
                      </router-link>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Methods</v-col>
                    <v-col>
                      {{ prediction.methods.map(a => a.name).join(", ") }}
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Settings</v-col>
                    <v-col>
                      <a class="text-decoration-underline" @click="settings = !settings">{{ settings ? 'Hide' : 'Show' }} Settings</a>
                      <pre v-if="settings">{{ prediction.config }}</pre>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Groups</v-col>
                    <v-col>
                      <a class="text-decoration-underline" @click="groups = !groups">{{ groups ? 'Hide' : 'Show' }} Groups</a>
                      <pre v-if="groups">{{ prediction.groups }}</pre>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Owner</v-col>
                    <v-col>{{ prediction.owner.username }}</v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Created</v-col>
                    <v-col>{{ prediction.created_at | dateFormat }}</v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="12" sm="2" md="1" class="font-weight-bold">Updated</v-col>
                    <v-col>{{ prediction.updated_at | dateFormat }}</v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-container>
</template>

<script>
import toolbar from "@/components/toolbar"
import AxButton from "@/components/button"
import frameHead from "@/components/head"
import frameInfo from "@/components/info"
import Regenerate from "@/components/regenerate"
import Predictions from "@/api/predictions"
import { RouterLink } from "vue-router"
export default {
  name: "Prediction",
  components: {
    toolbar,
    AxButton,
    frameHead,
    frameInfo,
    Regenerate,
  },
  data() {
    return {
      prediction: null,
      head: {
        data: [],
        schema: {}
      },
      info: {
        data: [],
        schema: {}
      },
      settings: false,
      groups: false,
      headLoaded: false,
      headLoading: false
    }
  },
  created() {
    Predictions.retrieve(this.$route.params.uuid).then(response => this.prediction = response.data)
    Predictions.info(this.$route.params.uuid).then(response => (this.info = typeof response.data == "string" ? JSON.parse(response.data) : response.data))
  },
  mounted() {
    this.onLoad()
  },
  methods: {
    onLoad() {
      if (this.headLoaded || this.headLoading) {
        return
      }
      
      this.headLoading = true
      Predictions.head(this.$route.params.uuid, this.head.data.length)
        .then((response) => {
          let head = typeof response.data == "string" ? JSON.parse(response.data) : response.data
          if (head.data && head.data.length > 0) {
            if (this.head.data.length === 0) {
              this.$set(this.head, 'data', head.data)
            } else {
              this.head.data.push(...head.data)
            }
            if (head.schema && Object.keys(head.schema).length > 0) {
              this.$set(this.head, 'schema', head.schema)
            }
            if (head.data.length < 50) {
              this.headLoaded = true
            }
          } else {
            this.headLoaded = true
          }
        })
        .finally(() => {
          this.headLoading = false
        })
    },
    onRegenerate(refresh) {
      let formData = {
        name: this.prediction.name,
        application: this.prediction.application.id,
        methods: this.prediction.methods.map(m => m.id),
        dataset: this.prediction.dataset.id,
        config: this.prediction.config,
        owner: this.user.id,
        refresh: refresh
      }
      Predictions.update(this.$route.params.uuid, formData).then(_ => {
        this.notify("Generating prediction...")
        this.$router.push({ name: "predictionList" })
      })
    },
    onDownload() {
      this.setBusy(true)
      Predictions.download(this.$route.params.uuid).then(response => {
        let csv = response.data
        var downloadLink = document.createElement("a")
        var blob = new Blob(["\ufeff", csv])
        var url = URL.createObjectURL(blob)
        downloadLink.href = url
        downloadLink.download = `${this.$route.params.uuid}.csv` //Name the file here
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
        this.setBusy(false)
      }).catch(() => {
        this.setBusy(false)
      })
    },
    onDump() {
      
      // this.setBusy(true)
      // Predictions.dump(this.$route.params.uuid).then(response => {
      //   // Create a blob from the response data (Excel file)
      //   var blob = new Blob([response.data], { 
      //     type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      //   })
      //   var url = URL.createObjectURL(blob)
        
      //   // Create download link and trigger download
      //   var downloadLink = document.createElement("a")
      //   downloadLink.href = url
      //   downloadLink.download = `${this.$route.params.uuid}.xlsx`
      //   document.body.appendChild(downloadLink)
      //   downloadLink.click()
      //   document.body.removeChild(downloadLink)
        
      //   // Clean up the object URL
      //   URL.revokeObjectURL(url)
      //   this.setBusy(false)
      // }).catch(() => {
      //   this.setBusy(false)
      // })
    },
    onSyncGroups() {
      Predictions.sync_groups(this.$route.params.uuid).then(_ => {
        this.notify('Prediction groups synchronized', 'success')
      })
    },
    onClone(){
      Predictions.clone(this.$route.params.uuid).then(_ => {
        this.notify('Prediction cloned')
        this.$router.push({ name: 'predictionList' } )
      })
    },
    onCopyApis() {
      Predictions.apis(this.$route.params.uuid).then(response => {
        const apis = response.data
        if (apis && apis.length > 0) {
          const apisText = apis.join('\n')
          navigator.clipboard.writeText(apisText).then(() => {
            this.notify(`Copied ${apis.length} APIs to clipboard`)
          }).catch(err => {
            this.notify('Failed to copy to clipboard')
          })
        } else {
          this.notify('No APIs found')
        }
      }).catch(err => {
        this.notify('Failed to retrieve APIs')
      })
    },
    onDelete() {
      Predictions.destroy(this.$route.params.uuid).then(_ => {
        this.notify('Prediction deleted', 'success')
        this.$router.push({ name: 'predictionList' })
      })
    }
  },
  computed: {
    tab: {
      set (tab) {
        this.$router.replace({ hash: tab })
      },
      get () {
        return this.$route.hash.replace('#', '')
      }
    },
    result() {
      if ('result' in this.prediction) return this.prediction.result
    },
    status() {
      if (this.prediction.status == "SUCCESS") return "success--text"
      else if (this.prediction.status == "RUNNING") return "success--text"
      else if (this.prediction.status == "REVOKED") return "warning--text"
      else if (this.prediction.status == "PENDING") return "primary--text"
      else if (this.prediction.status == "FAILURE") return "error--text"
      else return ""
    },
    targets() {
      if (this.prediction) {
        let targets = []
        for (let [key, value] of Object.entries(this.prediction.config.targets)) {
          if (value != null) targets.push({ text: this.capitalize(key), value: key })
        }
        return targets
      }
      return []
    },
    methods() {
      if (this.prediction) {
        let methods = []
        this.prediction.methods.forEach(element => {
          methods.push({ text: element.name, value: element.id })
        })
        return methods
      }
      return []
    },
    errors() {
      if (this.result) {
        var result = JSON.parse(this.result.result)
        if (typeof result === "object" && result !== null)
          if (Array.isArray(result["exc_message"])) return `${result["exc_type"]}: ${result["exc_message"][0]}`
          else return result["exc_type"]
        return result
      }
      return null
    },
  }
}
</script>